import Vue from 'vue'
import Vuex from 'vuex'
import {getName, isExpoApp, getUrlParameterThis, setCookie, removeSearchParameter, eraseCookie } from '../util.js'
import * as Sentry from '@sentry/vue';
import { getCurrentHub } from '@sentry/vue';
import { initializeSentry } from '../services/SentrySetup';

function initStore() {
    Vue.use(Vuex);

    const saveInLocalStorage = ['editMenu', 'carts', 'selectedSiteId', 'codeMaximizeWindow', 'language'];

    let updateUserData = (user) => {
        user.name = getName(user);

        let isBlocked = (user, type) => {
            let blocks = user.blocks?.filter(t => t[type]);
            if (!blocks || !blocks.length) {
                return null;
            }
            let block = blocks.find(x => !x.toDate);
            if (block) {
                return block; // Blocked until further notice
            }
            blocks = blocks.concat();
            blocks.sort((a, b) => Date.newFull(a.toDate) - Date.newFull(b.toDate));
            return blocks[0];
        }

        user.isBlocked = (type) => isBlocked(user, type);

        user.hasConsent = () => {
            const selectedSite = store.state.settings?.sites?.find(
                site => site.id === store.state.selectedSiteId
            );
            const legalEntityId = selectedSite?.legal_entity?.id;

            if (legalEntityId && user.consents) {
                return !!user.consents[legalEntityId];
            }
            // Fallback to legacy consent field
            return !!user.consent;
        };

        if (user.canBookFor) {
            user.canBookFor.forEach(x => {
                x.isBlocked = (type) => isBlocked(x, type);
            })
            user.canBookFor = user.canBookFor.sort((a,b) => {
                if (a.id == user.id) {
                    return -1;
                }

                if (b.id == user.id) {
                    return 1;
                }

                return a.name.localeCompare(b.name);
            })
        }
        let self = user.canBookFor?.find(x => x.id == user.id)
        if (self) {
            Object.assign(self, user);
        }

        const sentry = getCurrentHub()?.getScope();
        sentry?.setUser({ id: user.id });
    };

    const store = new Vuex.Store({
        state: {
        // Module: general
            now30s: new Date(),              // Current date, updated every 30 second
            showMobileMenu: false,
            checkinDialog: {
                show: false,
                onlyShowQrScanner: false,
                allSites: false,
                resources: []
            },
            invalidCartDialog: {
                show: false,
                cartLegalEntityName: "",
                conflictingLegalEntityName: "",
            },
            errorDialog: {
                show: false,
                title: '',
                text: '',
                action: null,
                actionText: 'Ok',
                color: 'error'
            },
            snackBar: {
                show: 0,
                text: ''
            },
            confirmDialog: {
                show: false,
                title: '',
                text: '',
                action: null,
                actionText: ''
            },
            language: null,
            defaultLanguage: 'sv',
            loadedLanguage: null,
            translations: {},
            translationsLoaded: false,
            googleMapsApiKey: 'AIzaSyAkUAkjyqsYAb-FCtpUY7WdetIQUv1-slQ', // Only used when flag-leaflet-maps is disabled
            settings: null,
            interests: null,
            loadingInterests: false,
            carts: {
            },
            checkoutDoneDialog: {
                show: false,
                orderconfirmation: {},
                totalFromServer: 0,
                paidByMethod: null,
                thankYouText: null
            },
            addUserDialog: {
                show: false,
                homesite_id: null,
                title: null,
                booking_fields: false,
                callback: null
            },
            changeUserDialog: {
                show: false,
                callback: null,
                user_id: null
            },
            checkoutHidden: false,

            // Module: user/account
            isCheckingIfLoggedIn: false,
            isLoggedIn: false,
            user: null,
            bookings: null,
            buyableCards: null,
            cards: null,
            isConnected: false,
            missingMemberFields: {
                show: false,
                disabled: false
            },
            selectedSiteId: null,

            // Module: edit
            editModeInitializing: false,
            editComponent: null,
            editComponentConfig: null,
            saving: false,
            needToSave: false,
            editMenu: false,
            fonts: null,
            copiedComponent: null,
            codeMaximizeWindow: false
        },
        mutations: {
        // Module: general
            updateNow(state) {
                state.now30s = new Date();
            },
            setShowMobileMenu(state, show) {
                state.showMobileMenu = show;
            },
            showErrorDialog(state, {title, text, action, actionText, color}) {
                if (!actionText) {
                    actionText = 'Ok';
                }
                if (!action) {
                    action = () => state.errorDialog.show = false;
                }
                if (!color) {
                    color = 'error';
                }

                Object.assign(state.errorDialog, {title, text, action, actionText, color, show: true});
            },
            hideErrorDialog(state) {
                state.errorDialog.show = false;
            },
            showSnackBar(state, {text, color}) {
                state.snackBar.show = true;
                state.snackBar.text = text;
                state.snackBar.color = color || 'error';
            },
            hideSnackBar(state) {
                state.snackBar.show = false;
            },
            showConfirmDialog(state, {title, text, action, actionColor = 'primary', actionText}) {
                state.confirmDialog.show = true;
                state.confirmDialog.title = title;
                state.confirmDialog.text = text;
                state.confirmDialog.action = action;
                state.confirmDialog.actionColor = actionColor;
                state.confirmDialog.actionText = actionText;
            },
            showMissingMemberFields(state, show) {
                state.missingMemberFields.show = show;
            },
            disableMissingMemberFields(state, disabled) {
                state.missingMemberFields.disabled = disabled;
            },
            setTranslations(state, translations) {
                state.translations = translations;
                state.translationsLoaded = true;
            },
            setSettings(state, settings) {
                state.settings = settings;
                settings.fields.forEach(field => {
                    if (field.type == 'select') {
                        field.data = field.data.map(i => ({
                            value: i.id || i,
                            text: i.name || i
                        }))
                        if (field.builtIn) {
                            field.format = 'translated';
                        }
                    }

                    if (field.type == 'date' && field.builtIn && field.name == 'birthdate') {
                        field.max = Date.today().yyyymmdd();
                    }
                })

                settings.webshopcategories?.forEach(cat => {
                    cat.parent = settings.webshopcategories.find(x => x.id == cat.parent_id)
                    let getTopCategory = (c) => {
                        if (c.parent && !c.parent.parent) {
                            return c.parent;
                        } else if (c.parent) {
                            return getTopCategory(c.parent)
                        } else {
                            return c;
                        }
                    }
                    cat.top_category = getTopCategory(cat);
                })
                if (!state.selectedSiteId && settings.sites && settings.sites.length) {
                    state.selectedSiteId = settings.sites[0].id;
                }

                initializeSentry(Vue, settings);
            },
            showCheckoutDoneDialog(state, {orderconfirmation, totalFromServer, paidByMethod, thankYouText}) {
                Object.assign(state.checkoutDoneDialog, {orderconfirmation, totalFromServer, paidByMethod, thankYouText, show: true});
            },
            showAddUserDialog(state, {homesite_id, title, booking_fields, callback}) {
                Object.assign(state.addUserDialog, {homesite_id, title, booking_fields, callback, show: true});
            },
            showChangeUserDialog(state, {callback, user_id}) {
                Object.assign(state.changeUserDialog, {callback, user_id, show: true});
            },
            showCheckinDialog(state, {resources, onlyShowQrScanner, allSites}) {
                state.checkinDialog.resources = resources || [];
                state.checkinDialog.onlyShowQrScanner = onlyShowQrScanner || false;
                state.checkinDialog.allSites = allSites || false;
                state.checkinDialog.show = true;
            },
            showInvalidCartDialog(
                state,
                { cartLegalEntityName, conflictingLegalEntityName }
            ) {
                state.invalidCartDialog.cartLegalEntityName =
                    cartLegalEntityName;
                state.invalidCartDialog.conflictingLegalEntityName =
                    conflictingLegalEntityName;
                state.invalidCartDialog.show = true;
            },
            setCheckoutHidden(state, hidden) {
                state.checkoutHidden = hidden;
            },

            // Module: user/account
            setLoggedIn (state, {isLoggedIn, user}) {
                state.isLoggedIn = isLoggedIn;
                if (isLoggedIn) {
                    updateUserData(user);
                    store.dispatch('setLanguage', {language: user.language, changeOnServer: false});
                } else {
                    eraseCookie('session');
                }
                state.user = isLoggedIn ? user : null;
                state.isCheckingIfLoggedIn = false;
                if (user && !state.selectedSiteId) {
                    state.selectedSiteId = user.site;
                }
            },
            setUserData (state, {user}) {
                updateUserData(user);
                store.dispatch('setLanguage', {language: user.language, changeOnServer: false});
                state.user = user;
            },
            setBookings( state, bookings) {
                state.bookings = bookings;
            },
            setIsConnected(state, isConnected) {
                state.isConnected = isConnected;
            },
            setCards(state, cards) {
                state.cards = cards;
            },
            setBuyableCards(state, cards) {
                state.buyableCards = cards;
            },

            // Module: edit
            setEditComponent(state, editComponent) {
                state.editComponent = editComponent;
                state.editComponentConfig = editComponent ? editComponent.$parent._props.config : null;
            },
            setEditModeInitializing(state, value) {
                state.editModeInitializing = value;
            },
            setSaving(state, value) {
                state.saving = value;
            },
            setNeedToSave(state, value) {
                state.needToSave = value;
            },
            setEditMenu(state, value) {
                state.editMenu = value;
            },
            setFonts(state, value) {
                state.fonts = value;
            },
            copyComponent(state, component) {
                state.copiedComponent = component;
            },
            setCodeMaximizeWindow(state, value) {
                state.codeMaximizeWindow = value;
            }
        },
        actions: {
            setVueRoot({state}) {
                saveInLocalStorage.forEach(prop => {
                    this._vm.$watch( () => state[prop], (value) => {
                        value = JSON.stringify(value);
                        window.localStorage.setItem('zoezi.' + prop, value);
                    }, {deep: true})
                })
            },
            logout({commit, state}) {
                this._vm.$api.post('/api/memberapi/logout').then(() => commit('setLoggedIn', {isLoggedIn: false}));
                state.carts = {};
                if(isExpoApp() && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'session', data: '' }));
                }
            },
            checkIfLoggedIn({commit, state}) {
                state.isCheckingIfLoggedIn = true;
                let session = getUrlParameterThis('session');
                if (session) {
                    setCookie('session', session, 365*10, true);
                    removeSearchParameter('session');
                }
                this._vm.$api.get('/api/memberapi/get/current').then((user) =>
                    commit('setLoggedIn', {isLoggedIn: true, user})
                ).catch(() => commit('setLoggedIn', {isLoggedIn: false}) )
            },
            googleSignIn({commit, state}, token) {
                this._vm.$api.post('/api/memberapi/google/login', null, {token, site_id: state.selectedSiteId}).then((response) => {
                    response.json().then(data => {
                        commit('setLoggedIn', {isLoggedIn: true, user: data});
                    });
                });
            },
            signinWithApple({commit, state}) {
                if (!state.isLoggedIn) {
                    let apple_nonce = localStorage.getItem('apple-signin-nonce');
                    if (apple_nonce) {
                        this._vm.$api.post('/api/memberapi/apple/login', apple_nonce).then((resp) => {
                            if (resp.status == 200) {
                                return resp.json();
                            } else {
                                commit('showSnackBar', {text: window.$translate('Login failed. Try again'), color: 'error'});
                            }
                        }).then(data => {
                            commit('setLoggedIn', {isLoggedIn: true, user: data});
                        }).catch(() => commit('setLoggedIn', {isLoggedIn: false}) )
                    }
                }
                localStorage.removeItem('apple-signin-nonce');
                removeSearchParameter('applesignin');
            },
            setEditComponentConfig({state}, {config, parentComponent}) {
                let locateComponent = (node) => {
                    if (node?.$options?.name == 'EditPanel') {
                        return null;
                    }
                    if (node?._props?.config == config) {
                        return node.$children[0];
                    }

                    let res = null;
                    node.$children.forEach(x => {
                        let c = locateComponent(x);
                        if (!res) {
                            res = c;
                        }
                    })

                    return res;
                };

                Vue.nextTick(() => {
                    let root = document.getElementsByClassName('zoezi-root')[0].__vue__;
                    let vuecomponent = locateComponent(root);

                    if (vuecomponent && config == vuecomponent.$parent._props.config) {
                        state.editComponent = vuecomponent;
                        state.editComponentConfig = vuecomponent.$parent._props.config;
                    } else {
                        if (parentComponent && parentComponent.findChild) {
                            parentComponent.findChild(config.id);
                            Vue.nextTick(() => {
                                let vuecomponent = locateComponent();
                                if (vuecomponent) {
                                    state.editComponent = vuecomponent;
                                    state.editComponentConfig = vuecomponent.$parent._props.config;
                                }
                            });
                        } else {
                            state.editComponent = null;
                            state.editComponentConfig = config;
                        }
                    }
                });
            },
            setLanguage({state}, {language, changeOnServer}) {
                if (!language || state.loadedLanguage == language) {
                    return;
                }

                this._vm.$translations.fetchLanguageFile(language).then(() => {
                    state.language = language;
                    state.loadedLanguage = language;
                    window.$vuetify.lang.current = language;

                    if (changeOnServer && state.user) {
                        this._vm.$api.post('/api/memberapi/change', {id: state.user.id, language});
                    }
                })
            },
            loadInterests({state}) {
                if (state.loadingInterests || state.interests) {
                    return;
                }
                state.loadingInterests = true;

                this._vm.$api.get('/api/memberapi/interest/get').then((interests) => {
                    state.interests = interests;
                    state.loadingInterests = false;
                })
            },
            showInvalidCartDialog(
                { state, commit, getters },
                { cartName, conflictingSiteId }
            ) {
                const cart = state.carts[cartName];
                const cartItem = cart.items[0];
                const cartLegalEntity = getters.getLegalEntityBySiteId(
                    cartItem.site_id
                );
                const conflictingLegalEntity =
                    getters.getLegalEntityBySiteId(conflictingSiteId);
                commit("showInvalidCartDialog", {
                    cartLegalEntityName: cartLegalEntity?.name || '',
                    conflictingLegalEntityName: conflictingLegalEntity?.name || '',
                });
            },
            addToCart({state, getters, dispatch}, {cartName, product_id, count, variant, price, site_id, course_id, answers, reservation, variants_for_included_products, rbservice_id, slots, extra_person, extended_info, tmp_id, rbooking_id, service, users, user_id, start_dates}) {
                cartName = cartName || '';
                site_id = site_id || state.selectedSiteId;
                if (!state.carts) {
                    state.carts = {};
                }
                let cart = state.carts[cartName];
                if (!cart) {
                    cart = {
                        items: [],
                        site_id,
                        showBottomBar: false,
                        showCheckout: false
                    };
                    Vue.set(state.carts, cartName, cart)
                }
                if (!count) {
                    count = 1;
                }

                let item =
                    product_id && !reservation
                        ? cart.items.find(
                              (i) =>
                                  i.product_id == product_id &&
                                  i.variant == variant &&
                                  i.site_id == site_id
                          )
                        : null;
                if (!item) {
                    item = {
                        product_id,
                        variant,
                        count: 0,
                        users: users
                            ? users.concat()
                            : new Array(count).fill(
                                  user_id || (state.user ? state.user.id : null)
                              ),
                        user_id,
                        start_dates: start_dates
                            ? start_dates.concat()
                            : new Array(count).fill(null),
                        course_id,
                        answers,
                        reservation,
                        variants_for_included_products,
                        rbservice_id,
                        slots,
                        extra_person,
                        extended_info,
                        tmp_id,
                        rbooking_id,
                        service,
                        site_id,
                    };
                    if (getters.hasModule('MultipleLegalEntities') && !getters.isSiteIdValidForCart(cartName, item.site_id)) {
                        dispatch("showInvalidCartDialog", {
                            cartName,
                            conflictingSiteId: item.site_id,
                        });
                        return;
                    } else {
                        cart.items.push(item);
                    }
                }

                if (price !== undefined) {
                    item.price = price;
                }

                item.count += count;

                state.carts = Object.assign({}, state.carts);

                try {
                    let ecommerceTrackingItem = {
                        id: 'p' + item.product_id,
                        count: count,
                    };
                    this._vm.$api.get('/api/public/trainingcard/type/get', {ids: item.product_id}).then(products => {
                        let product = products.find((p) => p.id == item.product_id);

                        if (product) {
                            ecommerceTrackingItem.price = product.price;
                            ecommerceTrackingItem.vat = product.vat;
                            ecommerceTrackingItem.name = variant != null ? product.name + ', ' + variant : product.name;
                            this._vm.$ecommercetracking.addToCart(ecommerceTrackingItem);
                        }
                    });
                } catch(error) {
                    Sentry.captureException(error);
                }
            },
            changeItemCountInCart({state}, {cartName, product_id, variant, tmp_id, count, product}) {
                // FIXME: Passing tmp_id is super ugly - better way would be to pass the whole item
                cartName = cartName || '';
                if (!state.carts) {
                    state.carts = {};
                }
                let cart = state.carts[cartName];
                if (cart === undefined) {
                    return;
                }

                let existing = cart.items.find(x => x.product_id == product_id && x.variant == variant && x.tmp_id == tmp_id)
                if (existing) {
                    existing.count = count;
                    if (existing.users) {
                        if (count > existing.users.length) {
                            existing.users = [...existing.users, ...Array(count - existing.users.length).fill(null)];
                        } else {
                            existing.users = existing.users.slice(0, count);
                        }
                    }
                    if (existing.start_dates) {
                        if (count > existing.start_dates.length) {
                            const basketCards = existing.start_dates.map((startDate, i) => ({
                                user_id: existing.users[i],
                                cardtype_id: product_id,
                                validToDate: startDate ? this._vm.$booking.getEndDateForCard(startDate, product) : null,
                                trainingsLeft: null
                            }));

                            const newDates = Array(count - existing.start_dates.length)
                                .fill(null)
                                .map((_, index) =>
                                    this._vm.$booking.getStartDateForCard(
                                        product,
                                        existing.users[existing.start_dates.length + index] || null,
                                        basketCards
                                    ).yyyymmdd()
                                );

                            existing.start_dates = [...existing.start_dates, ...newDates];
                        } else {
                            existing.start_dates = existing.start_dates.slice(0, count);
                        }
                    }
                    if (count == 0) {
                        cart.items.splice(cart.items.indexOf(existing), 1)
                    }
                }

                state.carts = Object.assign({}, state.carts);
            },
            changeItemUsersInCart({state}, {cartName, product_id, variant, users}) {
                cartName = cartName || '';
                if (!state.carts) {
                    state.carts = {};
                }
                let cart = state.carts[cartName];
                if (cart === undefined) {
                    return;
                }

                let existing = cart.items.find(x => x.product_id == product_id && x.variant == variant)
                if (existing) {
                    existing.users = users.concat();
                }

                state.carts = Object.assign({}, state.carts);
            },
            removeCart({state}, {cartName}) {
                cartName = cartName || '';
                if (!state.carts) {
                    state.carts = {};
                }
                let cart = state.carts[cartName];
                if (cart) {
                    delete state.carts[cartName];
                }

                state.carts = Object.assign({}, state.carts);
            },
            setCartVisibility({state}, {cartName, showBottomBar, showCheckout}) {
                cartName = cartName || '';
                if (!state.carts) {
                    state.carts = {};
                }
                let cart = state.carts[cartName];
                if (showBottomBar || showCheckout) {
                    Object.values(state.carts).forEach(cart => {
                        cart.showBottomBar = false;
                        cart.showCheckout = false;
                    })
                }
                if (cart) {
                    cart.showBottomBar = showBottomBar;
                    cart.showCheckout = showCheckout;
                }
                state.carts = Object.assign({}, state.carts);
            },
            setSite({state}, {siteId, changeOnServer=true}) {
                state.selectedSiteId = siteId;

                if (changeOnServer && state.user && state.user.site != siteId) {
                    this._vm.$api.post('/api/memberapi/setsite', null, {id: siteId});
                }
                if (state.user) state.user.site = siteId;
            }
        },
        getters: {
            comingBookings: (state) => {
                if (!state.bookings) {
                    return null
                }

                let res = {};
                res.workouts = state.bookings.workouts?.filter(w => w.startTime >= state.now30s) || [];
                res.resourcebookings = [];

                state.bookings.resourcebookings?.forEach(rcat => {
                    res.resourcebookings = res.resourcebookings.concat(rcat.bookings.filter(rb => !rb.cancelled && Date.newFull(rb.time) >= state.now30s))
                })

                res.coursebookings = state.bookings.coursebookings
                res.coursewatches = state.bookings.coursewatches

                return res;
            },
            getLegalEntityBySiteId: (state) => (siteId) => {
                return state.settings?.sites?.find((s) => s.id == siteId)
                    ?.legal_entity;
            },
            isSiteIdValidForCart: (state, getters) => (cartName, siteId) => {
                const cart = state.carts?.[cartName];
                if (!cart || cart.items.length == 0) {
                    return true;
                }
                // Some items don't have a site_id, e.g. memberships. Allow adding those items to any cart.
                if (!siteId) {
                    return true;
                }
                return (
                    getters.getLegalEntityBySiteId(siteId)?.id ==
                    getters.getLegalEntityBySiteId(cart.items[0].site_id)?.id
                );
            },
            selectedSiteId: (state) => state.selectedSiteId || null,
            hasModule: (state) => (moduleName) => !!state.settings?.modules?.[moduleName],
            selectedLegalEntityId: (state, getters) => getters.getLegalEntityBySiteId(state.selectedSiteId)?.id
        }
    })

    saveInLocalStorage.forEach(prop => {
        try {
            let item = window.localStorage.getItem('zoezi.' + prop);
            if (item !== undefined) {
                item = JSON.parse(item);
                store.state[prop] = item;
            }
        } catch (e) {
        // Most probably inside an iframe
        }
    })

    setInterval(() => store.commit('updateNow'), 30*1000)

    window.$store = store;

    return store;
}

export {initStore};
